import type { FetchTokenFn } from '@zealot/shared/utils/fetchTokenWithRetry';

// gloss
const SKIP_RETRY_PATTERNS = ['/auth/', '/oauth/'];

const shouldSkipRetry = (url: string): boolean => SKIP_RETRY_PATTERNS.some((pattern) => url.includes(pattern));

// gloss
type RetryRequestOptions = {
  fetch?: typeof fetch;
  serializedBody?: unknown;
  body?: unknown;
};

// gloss
type AuthRetryOptions = {
  onBeforeRefresh?: () => void;
  // why: onAuthFailure must only fire when a freshly-refreshed token is still rejected — a null
  // why: refresh (network blip) must not trigger it.
  onAuthFailure?: () => void;
  onError?: (error: unknown, url: string) => void;
};

// gloss
const buildRetryRequest = (request: Request, opts: RetryRequestOptions): Request => {
  if (!request.bodyUsed) {
    return new Request(request, {
      headers: new Headers(request.headers),
      duplex: 'half',
    } as RequestInit);
  }

  return new Request(request.url, {
    method: request.method,
    headers: new Headers(request.headers),
    body: (opts.serializedBody ?? opts.body) as RequestInit['body'],
    redirect: 'follow',
    duplex: 'half',
  } as RequestInit);
};

// gloss
const REFRESH_TIMEOUT_MS = 15_000;

// gloss
export const createAuthRetryInterceptor = (
  fetchTokenRef: { current: FetchTokenFn },
  options: AuthRetryOptions = {},
) => {
  let refreshPromise: Promise<string | null | undefined> | null = null;

  const getOrStartRefresh = (): Promise<string | null | undefined> => {
    if (refreshPromise) return refreshPromise;

    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), REFRESH_TIMEOUT_MS));
    refreshPromise = Promise.race([fetchTokenRef.current(), timeout]).finally(() => {
      refreshPromise = null;
    });

    return refreshPromise;
  };

  return async (response: Response, request: Request, opts: RetryRequestOptions) => {
    if (response.status !== 401 || shouldSkipRetry(request.url)) return response;

    try {
      options.onBeforeRefresh?.();
      const freshToken = await getOrStartRefresh();

      if (!freshToken) return response;

      const retryRequest = buildRetryRequest(request, opts);
      retryRequest.headers.set('Authorization', `Bearer ${freshToken}`);

      const _fetch = opts.fetch ?? fetch;
      const retryResponse = await _fetch(retryRequest);

      if (retryResponse.status === 401) {
        options.onAuthFailure?.();
      }

      return retryResponse;
    } catch (error) {
      options.onError?.(error, request.url);
      return response;
    }
  };
};
